import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type {
  CreateOrderItemRequest, Order, OrderItem, OrderType, PagedResult, TakeOutDeliveryOrder,
} from '../types/orders';
import type { Table, TableStatus } from '../types/tables';
import type { Product, ProductVariant, ProductWithVariants, VariantGroup } from '../types/products';
import type { DailyMenu } from '../types/dailyMenu';
import { loadOfflineResponse, offlineCacheKey } from './offlineCache';
import { enqueueOffline } from './offlineQueue';
import {
  currentOfflineOrderByTable, formatOfflineFolio, getOfflineOrder, getOfflineOrderByTable, listOfflineOrders,
  mergeOfflineTables, nextOfflineFolio, offlineTableState, saveOfflineOrder, OfflineOrderSnapshot,
} from './offlineOrders';
import { buildOfflineCatalog, OfflinePricingError, priceOfflineItem, sumOfflineItems } from './offlinePricing';
import { offlineNowIso } from './offlineClock';
import { formatAddress } from '../utils/formatAddress';

/**
 * La caja trabajando sin servidor.
 *
 * Esta capa se pone entre las pantallas y la red: cuando no hay servidor, responde a las
 * lecturas con lo guardado (mesas y órdenes, ya con lo que se hizo sin red) y **encola**
 * las escrituras devolviendo lo que el servidor habría devuelto. Por eso Mesas, Comandear,
 * Cobrar, Nueva orden y Órdenes no cambian ni una línea: siguen creyendo que hablan con el
 * servidor.
 *
 * Solo intercepta lo que se puede sostener sin red: abrir mesa, comandar, precuenta, cobrar,
 * liberar la mesa cobrada, y para llevar y domicilio. Todo lo demás —cancelar renglones,
 * descuentos, dividir cuenta, facturar— falla como siempre y el aviso de "Sin conexión"
 * explica por qué.
 */
export interface OfflineIdentity {
  userId: string;
  restaurantId: string;
  userName: string;
}

type Handled = { data: unknown } | { error: FetchBaseQueryError };

const asError = (message: string): Handled => ({
  error: { status: 409, data: { message } } as FetchBaseQueryError,
});

const requestOf = (args: string | FetchArgs) => {
  const url = typeof args === 'string' ? args : args.url;
  const method = (typeof args === 'string' ? 'GET' : args.method ?? 'GET').toUpperCase();
  const body = (typeof args === 'string' ? undefined : args.body) as Record<string, any> | undefined;
  return { url, method, body };
};

// ── Catálogo guardado ────────────────────────────────────────────────────────

const cached = async <T>(identity: OfflineIdentity, url: string): Promise<T | undefined> =>
  (await loadOfflineResponse(offlineCacheKey(identity.userId, identity.restaurantId, url))) as T | undefined;

const loadCatalog = async (identity: OfflineIdentity) => {
  const [products, groups, menus, variants] = await Promise.all([
    cached<Product[]>(identity, '/products?availableOnly=false'),
    cached<VariantGroup[]>(identity, '/products/variant-groups'),
    cached<DailyMenu[]>(identity, '/daily-menus/active'),
    cached<ProductVariant[]>(identity, '/products/variants'),
  ]);
  return buildOfflineCatalog(products ?? [], groups ?? [], menus ?? [], variants ?? []);
};

/**
 * Los modificadores de un producto, armados con la carta guardada.
 *
 * El modal de variantes pide `/products/{id}/with-variants` producto por producto, y esa
 * respuesta solo existía sin red si ese producto se había abierto antes con red: un
 * producto con modificadores no se podía agregar. La carta completa ya está guardada
 * (productos, todos los grupos y todas las variantes), así que aquí se arma la misma
 * respuesta. Sale de los mismos datos con que offlinePricing pone el precio, así que lo que
 * el modal ofrece es justo lo que se puede cobrar.
 *
 * null si falta el producto o los grupos: entonces baseApi prueba con la copia exacta de
 * esa URL, si la hay.
 */
async function productGroups(productId: string, identity: OfflineIdentity): Promise<VariantGroup[] | null> {
  const groups = await cached<VariantGroup[]>(identity, '/products/variant-groups');
  if (!groups) return null;
  return groups
    .filter((g) => g.productId === productId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

async function productWithVariants(productId: string, identity: OfflineIdentity): Promise<ProductWithVariants | null> {
  const products = await cached<Product[]>(identity, '/products?availableOnly=false');
  const product = products?.find((p) => p.id === productId);
  const variantGroups = await productGroups(productId, identity);
  if (!product || !variantGroups) return null;

  // Todas las del producto, también las sueltas sin grupo (el modal las muestra aparte).
  // Una caja que no ha vuelto a bajar la carta desde esta versión no tiene la lista
  // completa: sin ella solo se ofrecen las de los grupos, que son las que se saben cobrar.
  const allVariants = await cached<ProductVariant[]>(identity, '/products/variants');
  const variants = allVariants
    ? allVariants.filter((v) => v.productId === productId)
    : variantGroups.flatMap((g) => g.variants);

  return { ...product, variants, variantGroups };
}

// ── Lecturas ─────────────────────────────────────────────────────────────────

const ACTIVE_ORDER_BY_TABLE = /^\/orders\/table\/([0-9a-f-]+)\/active$/i;
const ORDER_BY_ID = /^\/orders\/([0-9a-f-]+)$/i;
const ORDERS_PAGED = /^\/orders\/paged(?:\?(.*))?$/i;
const TABLE_BY_ID = /^\/tables\/([0-9a-f-]+)$/i;
const PRODUCT_WITH_VARIANTS = /^\/products\/([0-9a-f-]+)\/with-variants$/i;
const PRODUCT_VARIANT_GROUPS = /^\/products\/([0-9a-f-]+)\/variant-groups$/i;

/**
 * La lista de Órdenes sin red: lo que esta caja tomó sin conexión, que es lo único que se
 * puede cobrar. Sin esto una orden para llevar tomada sin red no aparecía en ningún lado.
 * Respeta el estado y la búsqueda; las fechas y el método de pago no aplican (todo es de
 * hoy y el cobro sin red no guarda el desglose hasta sincronizar).
 */
async function pagedOfflineOrders(query: string, identity: OfflineIdentity): Promise<Handled> {
  const params = new URLSearchParams(query);
  const status = params.get('status');
  const search = params.get('searchTerm')?.trim().toLowerCase();
  const pageNumber = Math.max(1, Number(params.get('pageNumber')) || 1);
  const pageSize = Math.max(1, Number(params.get('pageSize')) || 20);

  const orders = (await listOfflineOrders())
    .filter((s) => !s.restaurantId || s.restaurantId === identity.restaurantId)
    .map((s) => s.order)
    .filter((o) => !status || o.status === status)
    .filter((o) => !search || [o.offlineFolioLabel, o.customerName, o.customerPhone, o.tableNumber]
      .some((field) => field?.toLowerCase().includes(search)))
    .sort((a, b) => b.openTime.localeCompare(a.openTime));

  const totalPages = Math.max(1, Math.ceil(orders.length / pageSize));
  const page: PagedResult<Order> = {
    items: orders.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
    totalCount: orders.length,
    pageNumber,
    pageSize,
    totalPages,
    hasPreviousPage: pageNumber > 1,
    hasNextPage: pageNumber < totalPages,
    totalAmount: orders.filter((o) => o.status !== 'Cancelled').reduce((sum, o) => sum + o.total, 0),
  };
  return { data: page };
}

/**
 * Nueva orden pide solo los disponibles (`availableOnly=true`), Comandear la carta completa.
 * En vez de bajar la carta dos veces, sin red se filtra la completa con la misma regla que
 * el servidor (`GetAvailableProductsAsync`): activo, disponible y de categoría activa.
 * Sin esto, Nueva orden salía vacía sin red si no se había abierto antes con red.
 */
async function availableProducts(identity: OfflineIdentity): Promise<Product[] | null> {
  const products = await cached<Product[]>(identity, '/products?availableOnly=false');
  if (!products) return null;
  const activeCategories = await cached<{ id: string }[]>(identity, '/categories?activeOnly=true');
  const activeIds = activeCategories ? new Set(activeCategories.map((c) => c.id)) : null;
  return products.filter((p) => p.isActive && p.isAvailable && (!activeIds || activeIds.has(p.categoryId)));
}

async function handleRead(url: string, identity: OfflineIdentity): Promise<Handled | null> {
  if (url === '/products?availableOnly=true') {
    const data = await availableProducts(identity);
    return data ? { data } : null;
  }

  if (url === '/tables') {
    const tables = await cached<Table[]>(identity, '/tables');
    if (!tables) return null;
    return { data: mergeOfflineTables(tables, await listOfflineOrders()) };
  }

  // "Liberar" vuelve a leer la mesa antes de mandar el cambio: tiene que verla como la ve
  // el mapa, no como quedó en la copia de antes de que se fuera la red.
  const tableById = TABLE_BY_ID.exec(url);
  if (tableById) {
    const table = (await cached<Table[]>(identity, '/tables'))?.find((t) => t.id === tableById[1]);
    if (!table) return null;
    const current = currentOfflineOrderByTable(await listOfflineOrders()).get(table.id);
    return { data: current ? { ...table, ...offlineTableState(current, table) } : table };
  }

  const byTable = ACTIVE_ORDER_BY_TABLE.exec(url);
  if (byTable) {
    const snapshot = await getOfflineOrderByTable(byTable[1]);
    // 404 es la respuesta normal del servidor para una mesa sin orden abierta.
    return snapshot ? { data: snapshot.order } : { error: { status: 404, data: null } as FetchBaseQueryError };
  }

  const paged = ORDERS_PAGED.exec(url);
  if (paged) return pagedOfflineOrders(paged[1] ?? '', identity);

  const byId = ORDER_BY_ID.exec(url);
  if (byId) {
    const snapshot = await getOfflineOrder(byId[1]);
    return snapshot ? { data: snapshot.order } : null;
  }

  const withVariants = PRODUCT_WITH_VARIANTS.exec(url);
  if (withVariants) {
    const data = await productWithVariants(withVariants[1], identity);
    return data ? { data } : null;
  }

  const groups = PRODUCT_VARIANT_GROUPS.exec(url);
  if (groups) {
    const data = await productGroups(groups[1], identity);
    return data ? { data } : null;
  }

  return null;
}

// ── Escrituras ───────────────────────────────────────────────────────────────

const CREATE_ORDER = '/orders';
const OPEN_TABLE = '/tables/open';
const CREATE_TAKEOUT = '/orders/takeout';
const CREATE_DELIVERY = '/orders/delivery';
const BULK_ITEMS = /^\/orders\/([0-9a-f-]+)\/items\/bulk$/i;
const PAYMENT = /^\/orders\/([0-9a-f-]+)\/payment$/i;
const TABLE_STATUS = /^\/tables\/([0-9a-f-]+)\/status$/i;
const ORDER_GUESTS = /^\/orders\/([0-9a-f-]+)\/guests$/i;

/** "la mesa 12", "para llevar de Lupita": para las etiquetas de la lista de revisión. */
const describeOrder = (order: Order): string => {
  if (order.type === 'TakeOut') return `para llevar de ${order.customerName ?? ''}`.trim();
  if (order.type === 'Delivery') return `domicilio de ${order.customerName ?? ''}`.trim();
  return `la mesa ${order.tableNumber}`;
};

/**
 * Abrir mesa, por los dos caminos que existen: "Abrir mesa" (POST /tables/open, con
 * comensales y mesero) y el alta implícita al comandar en una mesa libre (POST /orders).
 */
async function handleOpenOrder(
  operation: 'createOrder' | 'openTable', orderId: string, tableId: string,
  body: Record<string, any>, identity: OfflineIdentity
): Promise<Handled> {
  if (await getOfflineOrderByTable(tableId)) {
    return asError('La mesa ya tiene una orden abierta');
  }

  const tables = (await cached<Table[]>(identity, '/tables')) ?? [];
  const table = tables.find((t) => t.id === tableId);

  // La mesa ya estaba ocupada cuando se fue la red: su orden vive en el servidor y aquí no
  // se sabe qué tiene. Abrir otra encima terminaba en dos órdenes para la misma mesa, la
  // segunda cobrada sin red y rechazada al sincronizar — con el cliente ya pagado y ido.
  if (table && table.status !== 'Available' && table.status !== 'Reserved') {
    return asError(
      'Esta mesa ya estaba abierta antes de perder la conexión. Su cuenta vive en el servidor: '
      + 'espera a que vuelva el internet para cobrarla.');
  }

  // Cobrada sin red y sin liberar: el servidor la deja sucia al cobrar y "Abrir mesa" la
  // rechazaría al sincronizar ("Table is not available"), igual que aquí con red.
  const current = currentOfflineOrderByTable(await listOfflineOrders()).get(tableId);
  if (operation === 'openTable' && current?.status === 'Closed' && current.tableStatus !== 'Available') {
    return asError('La mesa está sucia: libérala antes de abrirla.');
  }
  const occurredAt = offlineNowIso();

  const offlineFolio = nextOfflineFolio();
  const order: Order = {
    id: orderId,
    tableId,
    tableNumber: table?.number ?? '',
    tableName: table?.number ?? '',
    orderNumber: 0, // lo asigna el servidor al sincronizar
    offlineFolioLabel: formatOfflineFolio(offlineFolio),
    type: 'DineIn',
    userId: identity.userId,
    userName: identity.userName,
    assignedWaiterId: body.waiterId,
    adults: body.adults,
    children: body.children,
    openTime: occurredAt,
    status: 'Open',
    total: 0,
    notes: body.notes,
    orderItems: [],
  };

  const snapshot: OfflineOrderSnapshot = {
    id: orderId, tableId, offlineFolio, status: 'Open', order, restaurantId: identity.restaurantId,
  };
  await saveOfflineOrder(snapshot);
  await enqueueOffline({
    operation,
    args: { ...body, occurredAt },
    occurredAt,
    orderId,
    tableId,
    label: `Mesa ${table?.number ?? ''} abierta (${formatOfflineFolio(snapshot.offlineFolio)})`,
  });

  // /tables/open no devuelve cuerpo; /orders devuelve la orden.
  return { data: operation === 'openTable' ? undefined : order };
}

/**
 * Para llevar y domicilio. A diferencia de una mesa, la orden nace con sus productos en una
 * sola petición, así que aquí se les pone precio y se encola todo junto. La comanda la
 * imprime la pantalla (Print Agent) como siempre.
 */
async function handleTakeOutDelivery(
  type: Extract<OrderType, 'TakeOut' | 'Delivery'>, body: Record<string, any>, identity: OfflineIdentity
): Promise<Handled | null> {
  const orderId: string | undefined = body.id;
  // Sin Id no se puede sincronizar sin arriesgar un duplicado: falla como siempre.
  if (!orderId) return null;

  // Un reintento de la misma pantalla: igual que el servidor, se contesta con la que ya está.
  const existing = await getOfflineOrder(orderId);
  if (existing) return { data: existing.order };

  const items = (body.orderItems ?? []) as CreateOrderItemRequest[];
  if (items.length === 0) return asError('Al menos un producto es requerido');

  // Lo mismo que el servidor rechaza, se rechaza aquí: al sincronizar ya sería tarde para
  // pedirle el dato a nadie.
  const isDelivery = type === 'Delivery';
  const customerName: string = (body.customerName ?? '').trim();
  if (isDelivery) {
    if (!body.customerId && (!customerName || !(body.customerPhone ?? '').trim())) {
      return asError('Nombre y teléfono del cliente son requeridos');
    }
    if (!body.deliveryAddressId && !body.newAddress) {
      return asError('Se requiere una dirección de entrega');
    }
  } else if (!customerName) {
    return asError('El nombre del cliente es requerido');
  }

  const catalog = await loadCatalog(identity);
  let priced: OrderItem[];
  try {
    priced = items.map((item) => priceOfflineItem(item, orderId, catalog));
  } catch (err) {
    return asError(err instanceof OfflinePricingError ? err.message : 'No se pudo calcular el precio sin conexión.');
  }

  const occurredAt = offlineNowIso();
  const offlineFolio = nextOfflineFolio();
  const virtualTable = isDelivery ? 'DELIVERY' : 'TAKEOUT';
  const order: TakeOutDeliveryOrder = {
    id: orderId,
    // La mesa virtual del servidor no viene en /tables; el cobro de estas órdenes no la pide.
    tableId: '',
    tableNumber: virtualTable,
    tableName: virtualTable,
    orderNumber: 0,
    offlineFolioLabel: formatOfflineFolio(offlineFolio),
    type,
    userId: identity.userId,
    userName: identity.userName,
    openTime: occurredAt,
    status: 'Open',
    total: sumOfflineItems(priced),
    orderItems: priced,
    customerId: body.customerId,
    customerName: customerName || undefined,
    customerPhone: body.customerPhone || undefined,
    deliveryAddressId: isDelivery ? body.deliveryAddressId : undefined,
    // Una dirección ya guardada del cliente solo trae su Id; la escribe el servidor al sincronizar.
    deliveryAddress: isDelivery && body.newAddress ? formatAddress(body.newAddress) : undefined,
    deliveryNotes: isDelivery ? body.deliveryNotes || undefined : undefined,
  };

  await saveOfflineOrder({
    id: orderId, tableId: '', offlineFolio, status: 'Open', order, restaurantId: identity.restaurantId,
  });
  await enqueueOffline({
    operation: isDelivery ? 'createDelivery' : 'createTakeOut',
    args: { ...body, occurredAt },
    occurredAt,
    orderId,
    label: `${isDelivery ? 'Domicilio' : 'Para llevar'} de ${customerName} (${formatOfflineFolio(offlineFolio)})`,
  });

  return { data: order };
}

async function handleAddItems(
  orderId: string, body: Record<string, any>, identity: OfflineIdentity
): Promise<Handled> {
  const snapshot = await getOfflineOrder(orderId);
  if (!snapshot) {
    // Una orden que abrió el servidor: sin red no se puede saber qué tiene ahora mismo, y
    // comandarle encima a ciegas es lo que produce cobros y comandas que no cuadran.
    return asError('Esta mesa se abrió con conexión; sin internet no se le pueden agregar productos.');
  }
  if (snapshot.status === 'Closed') {
    return asError('La orden ya se cobró.');
  }

  const catalog = await loadCatalog(identity);
  const occurredAt = offlineNowIso();
  let priced: OrderItem[];
  try {
    priced = (body.items as CreateOrderItemRequest[]).map((item) => priceOfflineItem(item, orderId, catalog));
  } catch (err) {
    return asError(err instanceof OfflinePricingError ? err.message : 'No se pudo calcular el precio sin conexión.');
  }

  const order: Order = {
    ...snapshot.order,
    orderItems: [...snapshot.order.orderItems, ...priced],
  };
  order.total = sumOfflineItems(order.orderItems);

  await saveOfflineOrder({ ...snapshot, order });
  await enqueueOffline({
    operation: 'addItems',
    args: { orderId, items: body.items, occurredAt },
    occurredAt,
    orderId,
    tableId: snapshot.tableId,
    label: `Comanda de la mesa ${snapshot.order.tableNumber} (${priced.length} producto(s))`,
  });

  return { data: order };
}

async function handlePayment(
  orderId: string, body: Record<string, any>, identity: OfflineIdentity
): Promise<Handled> {
  const snapshot = await getOfflineOrder(orderId);
  if (!snapshot) {
    return asError('Esta orden se abrió con conexión; sin internet no se puede cobrar.');
  }
  if (snapshot.status === 'Closed') {
    return asError('La orden ya se cobró.');
  }

  const occurredAt = offlineNowIso();
  const tip = Number(body.tip) || 0;
  const order: Order = { ...snapshot.order, status: 'Closed', closeTime: occurredAt };

  await saveOfflineOrder({ ...snapshot, status: 'Closed', order });
  await enqueueOffline({
    operation: 'payment',
    args: { ...body, orderId, occurredAt },
    occurredAt,
    orderId,
    tableId: snapshot.tableId || undefined,
    label: `Cobro de ${describeOrder(snapshot.order)} (${formatOfflineFolio(snapshot.offlineFolio)})`,
  });

  return {
    data: {
      orderId,
      tableId: snapshot.tableId,
      totalAmount: order.total + tip,
      tipAmount: tip,
      discountAmount: 0,
      payments: body.payments ?? [],
      processedAt: occurredAt,
      receiptId: formatOfflineFolio(snapshot.offlineFolio),
      isReplay: false,
    },
  };
}

/**
 * Precuenta, reabrir y liberar: el estado de una mesa cuya orden se tomó sin red.
 *
 * Solo esas tres transiciones y solo en mesas con orden de esta caja. Una mesa que ya
 * estaba ocupada o sucia al irse la red es del servidor, y cambiarle el estado a ciegas es
 * como se pierden cuentas (la orden 2205 de Marisa).
 */
async function handleTableStatus(tableId: string, status: TableStatus): Promise<Handled> {
  const current = currentOfflineOrderByTable(await listOfflineOrders()).get(tableId);
  if (!current) {
    return asError('Sin conexión solo se puede cambiar el estado de las mesas que se abrieron sin conexión en esta computadora.');
  }

  let tableStatus: OfflineOrderSnapshot['tableStatus'];
  let what: string;
  if (current.status === 'Open' && status === 'PreCheck') {
    tableStatus = 'PreCheck';
    what = 'en precuenta';
  } else if (current.status === 'Open' && status === 'Occupied') {
    tableStatus = undefined;
    what = 'reabierta';
  } else if (current.status === 'Closed' && status === 'Available') {
    tableStatus = 'Available';
    what = 'liberada';
  } else {
    return asError('Ese cambio de estado necesita conexión.');
  }

  // Ya estaba así: nada que mandar.
  if (current.tableStatus === tableStatus) return { data: undefined };

  const occurredAt = offlineNowIso();
  await saveOfflineOrder({ ...current, tableStatus });
  await enqueueOffline({
    operation: 'tableStatus',
    args: { id: tableId, status: { status } },
    occurredAt,
    orderId: current.id,
    tableId,
    label: `Mesa ${current.order.tableNumber} ${what} (${formatOfflineFolio(current.offlineFolio)})`,
  });

  return { data: undefined };
}

/**
 * Devuelve la respuesta que la pantalla habría recibido del servidor, o null si esta
 * petición no se puede atender sin red (y entonces falla como siempre).
 */
export async function handleOfflineRequest(
  args: string | FetchArgs, identity: OfflineIdentity
): Promise<Handled | null> {
  const { url, method, body } = requestOf(args);

  if (method === 'GET') return handleRead(url, identity);

  // Corregir comensales no se encola: sin esto llegaba el error genérico del modal.
  // Los de la apertura sí se guardan sin red; esto es solo la corrección.
  if (method === 'PATCH' && ORDER_GUESTS.test(url)) {
    return asError('Sin conexión no se pueden corregir los comensales. '
      + 'Espera a que vuelva el internet y vuelve a intentar.');
  }

  if (method === 'PATCH' && body?.status) {
    const tableStatus = TABLE_STATUS.exec(url);
    if (tableStatus) return handleTableStatus(tableStatus[1], body.status);
    return null;
  }

  // Editar una orden es un PUT completo, que la cola no manda nunca (docs/modo-offline.md).
  // Sin esto la pantalla solo decía "Error al procesar la orden".
  if (method === 'PUT') {
    const byId = ORDER_BY_ID.exec(url);
    if (byId && await getOfflineOrder(byId[1])) {
      return asError('Sin conexión no se pueden cambiar los productos de esta orden. '
        + 'Espera a que vuelva el internet, o toma una orden nueva con lo que falta.');
    }
    return null;
  }

  if (method !== 'POST' || !body) return null;

  if (url === CREATE_ORDER && body.id && body.tableId) {
    return handleOpenOrder('createOrder', body.id, body.tableId, body, identity);
  }
  if (url === OPEN_TABLE && body.orderId && body.tableId) {
    return handleOpenOrder('openTable', body.orderId, body.tableId, body, identity);
  }
  if (url === CREATE_TAKEOUT) return handleTakeOutDelivery('TakeOut', body, identity);
  if (url === CREATE_DELIVERY) return handleTakeOutDelivery('Delivery', body, identity);

  const bulk = BULK_ITEMS.exec(url);
  if (bulk) return handleAddItems(bulk[1], body, identity);

  const payment = PAYMENT.exec(url);
  if (payment) return handlePayment(payment[1], body, identity);

  return null;
}
